import React, { useState, useEffect } from 'react';
import {
  AppBar,
  Toolbar,
  Typography,
  IconButton,
  Menu,
  MenuItem,
  Box,
  Chip,
  Avatar,
  Divider,
} from '@mui/material';
import {
  Menu as MenuIcon,
  AccountCircle,
  Logout,
  AccessTime,
  Person,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import api from '../../services/api';

/**
 * Header mejorado con reloj, fecha, nombre y rol del usuario
 * Path: backoffice-web/src/components/Layout/Header.js
 * Última modificación: 2025-12-31 02:30
 * Dev: cpalacios01@gmail.com | +595994648273
 */

export default function Header({ onMenuClick }) {
  const navigate = useNavigate();
  const [anchorEl, setAnchorEl] = useState(null);
  const [user, setUser] = useState(null);
  const [currentTime, setCurrentTime] = useState(new Date());

  const roles = {
    1: { nombre: 'Administrador', color: 'error' },
    2: { nombre: 'Conductor', color: 'primary' },
    3: { nombre: 'Rendidor', color: 'warning' },
  };

  useEffect(() => {
    fetchUserData();
    
    // Actualizar reloj cada segundo
    const timer = setInterval(() => {
      setCurrentTime(new Date());
    }, 1000);

    return () => clearInterval(timer);
  }, []);

  const fetchUserData = async () => {
    try {
      const response = await api.get('/me');
      setUser(response.data.user || response.data);
    } catch (error) {
      console.error('Error al cargar datos del usuario:', error);
    }
  };

  const handleMenu = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleProfile = () => {
    handleClose();
    navigate('/perfil');
  };

  const handleLogout = () => {
    handleClose();
    localStorage.removeItem('admin_token');
    navigate('/login');
  };

  const formatDate = (date) => {
    const options = {
      weekday: 'long',
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    };
    return date.toLocaleDateString('es-PY', options);
  };

  const formatTime = (date) => {
    return date.toLocaleTimeString('es-PY', {
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
    });
  };

  const roleInfo = user ? (roles[user.role_id] || { nombre: 'Usuario', color: 'default' }) : null;

  return (
    <AppBar 
      position="fixed" 
      sx={{ 
        zIndex: (theme) => theme.zIndex.drawer + 1,
        width: { sm: `calc(100% - 240px)` },
        ml: { sm: `240px` },
      }}
    >
      <Toolbar>
        {/* Botón de menú móvil */}
        <IconButton
          color="inherit"
          edge="start"
          onClick={onMenuClick}
          sx={{ mr: 2, display: { sm: 'none' } }}
        >
          <MenuIcon />
        </IconButton>

        <Typography variant="h6" component="div" sx={{ flexGrow: 0, mr: 4 }}>
          Panel Administrativo
        </Typography>

        {/* Reloj y Fecha */}
        <Box sx={{ flexGrow: 1, display: { xs: 'none', md: 'flex' }, alignItems: 'center', gap: 2 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <AccessTime />
            <Box>
              <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
                {formatTime(currentTime)}
              </Typography>
              <Typography variant="caption" sx={{ fontSize: '0.7rem' }}>
                {formatDate(currentTime)}
              </Typography>
            </Box>
          </Box>
        </Box>

        {/* Información del Usuario */}
        {user && (
          <Box sx={{ display: { xs: 'none', sm: 'flex' }, alignItems: 'center', gap: 2, mr: 2 }}>
            <Box sx={{ textAlign: 'right' }}>
              <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
                {user.nombre}
              </Typography>
              <Chip
                label={roleInfo?.nombre}
                color={roleInfo?.color}
                size="small"
                sx={{ height: 20, fontSize: '0.7rem' }}
              />
            </Box>
            <Avatar
              sx={{
                bgcolor: roleInfo?.color + '.main',
                width: 40,
                height: 40,
              }}
            >
              {user.nombre?.charAt(0).toUpperCase() || 'U'}
            </Avatar>
          </Box>
        )}

        {/* Menú de Usuario */}
        <div>
          <IconButton
            size="large"
            aria-label="cuenta del usuario"
            aria-controls="menu-appbar"
            aria-haspopup="true"
            onClick={handleMenu}
            color="inherit"
          >
            <AccountCircle />
          </IconButton>
          <Menu
            id="menu-appbar"
            anchorEl={anchorEl}
            anchorOrigin={{
              vertical: 'bottom',
              horizontal: 'right',
            }}
            keepMounted
            transformOrigin={{
              vertical: 'top',
              horizontal: 'right',
            }}
            open={Boolean(anchorEl)}
            onClose={handleClose}
          >
            {user && (
              <Box sx={{ px: 2, py: 1 }}>
                <Typography variant="subtitle2" fontWeight="bold">
                  {user.nombre}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  {user.email}
                </Typography>
              </Box>
            )}
            <Divider />
            <MenuItem onClick={handleProfile}>
              <Person sx={{ mr: 1 }} fontSize="small" />
              Mi Perfil
            </MenuItem>
            <MenuItem onClick={handleLogout}>
              <Logout sx={{ mr: 1 }} fontSize="small" />
              Cerrar Sesión
            </MenuItem>
          </Menu>
        </div>
      </Toolbar>
    </AppBar>
  );
}

// cpalacios01@gmail.com | +595994648273
